import AnalyticsEvents from './AnalyticsEvents';

/**
 * @namespace Analytics
 */

/**
 * @typedef Item
 * @type {Object}
 * @property {string} item_id
 * @property {string} item_name
 * @property {string} affiliation
 * @property {string} coupon
 * @property {string} currency
 * @property {number} discount
 * @property {number} index
 * @property {string} item_brand
 * @property {string} item_category
 * @property {string} item_list_id
 * @property {string} item_list_name
 * @property {string} item_variant
 * @property {string} location_id
 * @property {number} price
 * @property {string} quantity
 */

export default class Analytics {
  constructor(currencyStore, uiStore) {
    this.analyticsInstance = new AnalyticsEvents(currencyStore, uiStore);
  }

  /**
   * @param {Object} data
   * @param {Array.<OrderCheckoutProduct>} data.productList
   * @param {number} data.value
   */
  addPaymentInfo(data) {
    this.analyticsInstance.addPaymentInfo(data);
  }

  /**
   * @param {Object} data
   * @param {Array.<OrderCheckoutProduct>} data.productList
   * @param {number} data.value
   */
  addShippingInfo(data) {
    this.analyticsInstance.addShippingInfo(data);
  }

  /**
   * @param {Array.<CartProduct|OrderCheckoutProduct>} productList
   * @param {number} value monetary value of the event
   */
  addToCart(productList, value) {
    this.analyticsInstance.addToCart(productList, value);
  }

  /**
   * @param {Array.<Item>} productList
   * @param {number} value monetary value of the event
   */
  addToProposal(productList, value) {
    this.analyticsInstance.addToProposal(productList, value);
  }

  /**
   * @param {Array.<Product>} productList
   * @param {number} value monetary value of the event
   */
  addToWishlist(productList, value) {
    this.analyticsInstance.addToWishlist(productList, value);
  }

  /**
   * @param {Array.<Item>} productList
   * @param {Object} actionField
   * @param {number} actionField.step
   * @param {string} actionField.option
   * @param {number} value monetary value of the event
   * @param {string} coupon
   */
  beginCheckout(productList, actionField, value, coupon) {
    this.analyticsInstance.beginCheckout(
      productList,
      actionField,
      value,
      coupon
    );
  }

  /**
   * @param {Array.<Item>} productList
   * @param {Object} actionField
   * @param {number} actionField.step
   * @param {string} actionField.option
   * @param {number} value monetary value of the event
   */
  beginProposal(productList, actionField, value) {
    this.analyticsInstance.beginProposal(productList, actionField, value);
  }

  /**
   * Send all products from the cart.
   * @deprecated use viewCart
   */
  cartStatus(cart) {
    this.analyticsInstance.cartStatus(cart);
  }

  /**
   * @param {Array.<Item>} productList
   * @param {Object} actionField
   * @param {number} actionField.step
   * @param {string} actionField.option
   * @param {number} value monetary value of the event
   * @param {string} coupon
   * @deprecated use beginCheckout
   */
  checkout(productList, actionField, value, coupon) {
    this.analyticsInstance.checkout(productList, actionField, value, coupon);
  }

  /**
   * Clear all products from the cart.
   */
  clearCart() {
    this.analyticsInstance.clearCart();
  }

  /**
   * FacebookPixel specific event
   * @param {Array.<Product>} productList
   * @param {number} value monetary value of the event
   */
  customizeProduct(productList, value) {
    this.analyticsInstance.customizeProduct(productList, value);
  }

  /**
   * @param {number} value monetary value of the event
   * @param {string} lead_source origin of the lead
   */
  generateLead(value, lead_source) {
    this.analyticsInstance.generateLead(value, lead_source);
  }

  /**
   * @param {string} groupId The ID of the group
   */
  joinGroup(groupId) {
    this.analyticsInstance.joinGroup(groupId);
  }

  // KCO (Kustom Checkout) analytics methods
  /**
   * @param {Object} eventData - KCO load event data
   * @param {Object} eventData.customer - Customer info
   * @param {string} eventData.customer.type - "person" or "organization"
   * @param {Object} eventData.shipping_address - Shipping address info
   * @param {string} eventData.shipping_address.country - Alpha3 country code
   * @param {string} eventData.shipping_address.postal_code - Postal code
   */
  kcoLoad(eventData) {
    this.analyticsInstance.kcoLoad(eventData);
  }

  /**
   * @param {Object} eventData - KCO customer event data
   * @param {string} eventData.type - "person" or "organization"
   */
  kcoCustomer(eventData) {
    this.analyticsInstance.kcoCustomer(eventData);
  }

  /**
   * @param {Object} eventData - KCO change event data
   * @param {string} [eventData.email] - Customer email
   * @param {string} [eventData.postal_code] - Postal code
   * @param {string} [eventData.country] - Country code
   */
  kcoChange(eventData) {
    this.analyticsInstance.kcoChange(eventData);
  }

  /**
   * @param {Object} eventData - KCO billing address change event data
   * @param {string} eventData.postal_code - Postal code
   * @param {string} eventData.country - Country code (alpha3)
   */
  kcoBillingAddressChange(eventData) {
    this.analyticsInstance.kcoBillingAddressChange(eventData);
  }

  /**
   * @param {Object} eventData - KCO shipping address change event data
   * @param {string} eventData.postal_code - Postal code
   * @param {string} eventData.country - Country code (alpha3)
   */
  kcoShippingAddressChange(eventData) {
    this.analyticsInstance.kcoShippingAddressChange(eventData);
  }

  /**
   * @param {Object} eventData - KCO shipping option change event data
   * @param {string} eventData.description - Shipping option description
   * @param {string} eventData.id - Shipping option ID
   * @param {string} eventData.name - Shipping option name
   * @param {number} eventData.price - Price in minor units (cents)
   * @param {string} [eventData.promo] - Promo text
   * @param {number} eventData.tax_amount - Tax amount in minor units
   * @param {number} eventData.tax_rate - Tax rate in minor units
   */
  kcoShippingOptionChange(eventData) {
    this.analyticsInstance.kcoShippingOptionChange(eventData);
  }

  /**
   * @param {Object} eventData - KCO order total change event data
   * @param {number} eventData.order_total - New order total in minor units
   */
  kcoOrderTotalChange(eventData) {
    this.analyticsInstance.kcoOrderTotalChange(eventData);
  }

  /**
   * @param {Object} eventData - KCO checkbox change event data
   * @param {string} eventData.key - Checkbox identifier
   * @param {boolean} eventData.checked - Checkbox state
   */
  kcoCheckboxChange(eventData) {
    this.analyticsInstance.kcoCheckboxChange(eventData);
  }

  /**
   * @param {Object} eventData - KCO network error event data (empty object)
   */
  kcoNetworkError(eventData) {
    this.analyticsInstance.kcoNetworkError(eventData);
  }

  /**
   * @param {boolean} eventData - KCO redirect initiated event data (true)
   */
  kcoRedirectInitiated(eventData) {
    this.analyticsInstance.kcoRedirectInitiated(eventData);
  }

  /**
   * @param {Object} eventData - KCO load confirmation event data (empty object)
   */
  kcoLoadConfirmation(eventData) {
    this.analyticsInstance.kcoLoadConfirmation(eventData);
  }

  /**
   * @param {string} method The method used to login.
   */
  login(method) {
    this.analyticsInstance.login(method);
  }

  pageNotFound(payload) {
    this.analyticsInstance.pageNotFound(payload);
  }

  paymentStart() {
    this.analyticsInstance.paymentStart();
  }

  /**
   * @param {Object} data
   * @param {Array.<Product>} data.productList An array with objects
   * @param {string} data.listName
   * @param {string} data.listId
   */
  productClick({ productList, listName = null, listId = null }) {
    this.analyticsInstance.productClick(listName, productList, listId);
  }

  /**
   * @param {string} listName
   * @param {Array} productList An array with objects in the format { position, product }.
   */
  productImpressions(listName, productList, listId) {
    this.analyticsInstance.productImpressions(listName, productList, listId);
  }

  /**
   * @param {Array} productDetail An array with objects in the format { product }.
   * @deprecated use viewItem
   */
  productDetail(productDetail) {
    this.analyticsInstance.productDetail(productDetail);
  }

  /**
   * @param {Array} bannerList An array with objects in the format { bannerZone, banner }.
   */
  promoView(bannerList) {
    this.analyticsInstance.promoView(bannerList);
  }

  /**
   * @param {Array} bannerList An array with objects in the format { bannerZone, banner }.
   */
  promotionClick(bannerList) {
    this.analyticsInstance.promotionClick(bannerList);
  }

  /**
   * @param {Order} currentOrder Current order model.
   */
  proposalSent(currentOrder) {
    this.analyticsInstance.proposalSent(currentOrder);
  }

  /**
   * @param {Order} currentOrder Current order model.
   */
  purchase(currentOrder) {
    this.analyticsInstance.purchase(currentOrder);
  }

  /**
   * @param {Object} data
   * @param {Object.Array<CartProduct>} productList List of removed products
   * @param {Object.<number>} value a Monetary value of the event
   */
  removeFromCart(data) {
    this.analyticsInstance.removeFromCart(data);
  }

  removeFromProposal(data) {
    this.analyticsInstance.removeFromProposal(data);
  }

  /**
   * @param {string} searchTerm
   */
  search(searchTerm) {
    this.analyticsInstance.search(searchTerm);
  }

  /**
   * @param {Object} gdprInfo
   * @param {Object.<string>} first_name
   * @param {Object.<string>} last_name
   * @param {Object.<string>} email
   * @param {Object.<string>} street_address
   * @param {Object.<string>} international_number
   * @param {Object.<boolean>} marketing_ban
   */
  sendGdprData(gdprInfo) {
    this.analyticsInstance.sendGdprData(gdprInfo);
  }

  /**
   * @param {Object} productClick
   * @param {Object.<Array>} productList An array with objects
   * @param {Object.<string>} listName
   * @param {Object.<string>} listId
   */
  selectItem({ productList, listName = null, listId = null }) {
    this.analyticsInstance.selectItem(listName, productList, listId);
  }

  /**
   * @param {Array} bannerList An array with objects in the format { bannerZone, banner }.
   */
  selectPromotion(bannerList) {
    this.analyticsInstance.selectPromotion(bannerList);
  }

  /**
   * @param {string} pageName
   */
  setPage(pageName) {
    this.analyticsInstance.setPage(pageName);
  }

  /**
   * @param {Object} content The method used to login.
   * @param {string} content.method
   * @param {string} content.content_type
   * @param {string} content.item_id
   */
  share(content) {
    this.analyticsInstance.share(content);
  }

  /**
   * @param {string} method The method used to login.
   */
  signup(method) {
    this.analyticsInstance.signup(method);
  }

  /**
   * When quick search suggestions are selected, the information is sent through this function.
   * Can be used to train suggestions.
   *
   * @param searchText
   * @param suggestionType
   * @param suggestionValue
   */
  suggestionSelection(searchText, suggestionType, suggestionValue) {
    this.analyticsInstance.suggestionSelection(
      searchText,
      suggestionType,
      suggestionValue
    );
  }

  /**
   * @param {Object} data
   * @param {Array.<Item>} data.productList
   * @param {number} data.value
   * @param {number} data.eventData
   */
  viewCart(data) {
    this.analyticsInstance.viewCart(data);
  }

  /**
   * @param {Object} data
   * @param {Array.<Item>} data.productList
   * @param {number} data.value
   */
  viewItem(data) {
    this.analyticsInstance.viewItem(data);
  }

  /**
   * @param {Array} bannerList An array with objects in the format { bannerZone, banner }.
   */
  viewPromotion(bannerList) {
    this.analyticsInstance.viewPromotion(bannerList);
  }

  /**
   * @param {Array} productDetail An array with objects in the format { product }.
   */
  viewItem(productDetail) {
    this.analyticsInstance.viewItem(productDetail);
  }

  /**
   * @param {string} listName
   * @param {Array} productList An array with objects in the format { position, product }.
   */
  viewItemList(listName, productList, listId) {
    this.analyticsInstance.viewItemList(listName, productList, listId);
  }

  /**
   * @param {number} value monetary value of the event
   * @param {Array.<Item>} productList An array with objects in the format { Item }.
   */
  viewProposal(value, productList) {
    this.analyticsInstance.viewProposal(value, productList);
  }

  /**
   * @param {CurrentOrder} currentOrder Current order model.
   */
  proposalSent(currentOrder) {
    this.analyticsInstance.proposalSent(currentOrder);
  }

  /**
   * @param {CurrentOrder} currentOrder Current order model.
   */
  purchase(currentOrder) {
    this.analyticsInstance.purchase(currentOrder);
  }
}
